import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PopularPosts = () => {
  const popularPosts = [
    {
      id: 1,
      title: "React Performance Optimization: A Complete Guide",
      excerpt: "Master the art of building lightning-fast React applications with advanced optimization techniques.",
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      readTime: "15 min read",
      views: 12500,
      publishedAt: "2024-12-15",
      category: "Technical Tutorials",
      difficulty: "Advanced",
      trending: true
    },
    {
      id: 2,
      title: "From Junior to Senior: My 5-Year Development Journey",
      excerpt: "Lessons learned, mistakes made, and strategies that accelerated my career growth in tech.",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      readTime: "8 min read",
      views: 8900,
      publishedAt: "2024-11-28",
      category: "Career Growth",
      difficulty: "Beginner",
      trending: false
    },
    {
      id: 3,
      title: "Building CodersHub: Community-First Development",
      excerpt: "How we built a thriving developer community from scratch and the lessons we learned.",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      readTime: "12 min read",
      views: 6700,
      publishedAt: "2024-11-10",
      category: "Community Building",
      difficulty: "Intermediate",
      trending: true
    },
    {
      id: 4,
      title: "React Native vs Flutter: 2025 Comparison",
      excerpt: "An honest comparison of mobile development frameworks based on real project experience.",
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      readTime: "10 min read",
      views: 5400,
      publishedAt: "2024-10-22",
      category: "Industry Insights",
      difficulty: "Intermediate",
      trending: false
    },
    {
      id: 5,
      title: "Mentoring 100+ Developers: What I've Learned",
      excerpt: "Key insights from mentoring developers at different stages of their careers.",
      image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      readTime: "7 min read",
      views: 4200,
      publishedAt: "2024-10-05",
      category: "Career Growth",
      difficulty: "Beginner",
      trending: false
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner':
        return 'bg-success/10 text-success';
      case 'intermediate':
        return 'bg-warning/10 text-warning';
      case 'advanced':
        return 'bg-error/10 text-error';
      default:
        return 'bg-accent/10 text-accent';
    }
  };

  return (
    <section className="bg-muted py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-12">
          {/* Section Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-2">
              <Icon name="TrendingUp" size={24} className="text-primary" />
              <h2 className="text-3xl font-brand-headline text-foreground">
                Popular Posts
              </h2>
            </div>
            <p className="text-lg text-text-secondary font-brand-body max-w-2xl mx-auto">
              Our most-read articles that continue to help developers grow their skills and advance their careers.
            </p>
          </div>

          {/* Featured Popular Post */}
          <div className="bg-card rounded-2xl border border-border overflow-hidden brand-shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Image */}
              <div className="relative h-64 lg:h-auto">
                <Image
                  src={popularPosts?.[0]?.image}
                  alt={popularPosts?.[0]?.title}
                  className="w-full h-full object-cover"
                />
                
                {/* Trending Badge */}
                {popularPosts?.[0]?.trending && (
                  <div className="absolute top-4 left-4">
                    <span className="flex items-center space-x-1 px-3 py-1 bg-error/90 backdrop-blur-sm text-white rounded-full text-sm font-brand-medium">
                      <Icon name="Flame" size={14} />
                      <span>Trending</span>
                    </span>
                  </div>
                )}

                {/* Stats Overlay */}
                <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3">
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="flex items-center space-x-1 text-foreground">
                      <Icon name="Eye" size={14} />
                      <span className="font-brand-medium">
                        {popularPosts?.[0]?.views > 1000 ? `${(popularPosts?.[0]?.views / 1000)?.toFixed(1)}k` : popularPosts?.[0]?.views}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1 text-text-secondary">
                      <Icon name="Clock" size={14} />
                      <span>{popularPosts?.[0]?.readTime}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 lg:p-12 flex flex-col justify-center space-y-6">
                <div className="space-y-4">
                  {/* Meta */}
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-brand-medium ${getDifficultyColor(popularPosts?.[0]?.difficulty)}`}>
                      {popularPosts?.[0]?.difficulty}
                    </span>
                    <span className="text-sm text-text-secondary">
                      {formatDate(popularPosts?.[0]?.publishedAt)}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl lg:text-3xl font-brand-headline text-foreground leading-tight">
                    {popularPosts?.[0]?.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-text-secondary font-brand-body text-lg">
                    {popularPosts?.[0]?.excerpt}
                  </p>
                </div>

                {/* CTA */}
                <div className="flex items-center space-x-4">
                  <Link
                    to="#"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-brand-medium hover:bg-primary/90 transition-brand"
                  >
                    <span>Read Full Article</span>
                    <Icon name="ArrowRight" size={16} />
                  </Link>
                  <button className="p-3 text-text-secondary hover:text-primary hover:bg-primary/10 rounded-lg transition-brand">
                    <Icon name="Bookmark" size={20} />
                  </button>
                  <button className="p-3 text-text-secondary hover:text-primary hover:bg-primary/10 rounded-lg transition-brand">
                    <Icon name="Share2" size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Popular Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularPosts?.slice(1)?.map((post, index) => (
              <article
                key={post?.id}
                className="bg-card rounded-xl border border-border overflow-hidden hover:brand-shadow transition-all duration-300 group"
              >
                {/* Rank Badge */}
                <div className="relative">
                  <Image
                    src={post?.image}
                    alt={post?.title}
                    className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  
                  <div className="absolute top-3 left-3">
                    <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-brand-subheading">
                      {index + 2}
                    </div>
                  </div>

                  {post?.trending && (
                    <div className="absolute top-3 right-3">
                      <Icon name="TrendingUp" size={16} className="text-error" />
                    </div>
                  )}
                </div>

                <div className="p-4 space-y-3">
                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs">
                    <span className={`px-2 py-1 rounded-full font-brand-medium ${getDifficultyColor(post?.difficulty)}`}>
                      {post?.difficulty}
                    </span>
                    <span className="text-text-secondary">
                      {formatDate(post?.publishedAt)}
                    </span>
                  </div>

                  {/* Title */}
                  <h4 className="text-sm font-brand-subheading text-foreground leading-tight line-clamp-2 group-hover:text-primary transition-brand">
                    {post?.title}
                  </h4>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-xs text-text-secondary pt-2 border-t border-border">
                    <div className="flex items-center space-x-1">
                      <Icon name="Eye" size={12} />
                      <span>{post?.views > 1000 ? `${(post?.views / 1000)?.toFixed(1)}k` : post?.views}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={12} />
                      <span>{post?.readTime}</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* View All Popular Posts */}
          <div className="text-center">
            <Link
              to="#"
              className="inline-flex items-center space-x-2 px-6 py-3 border border-primary/20 text-primary rounded-lg font-brand-medium hover:bg-primary/10 transition-brand"
            >
              <span>View All Popular Posts</span>
              <Icon name="ArrowRight" size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PopularPosts;